import { ArrowDownLeft, ArrowUpRight, FileWarning } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../shared/format'
import type { Transaction } from '../types'
import { useFinanceLookups } from '../ui'
import { CategoryDot, Money, PayStatusBadge } from './FinanceBits'

type Props = { rows: Transaction[]; canWrite: boolean; menu: (tx: Transaction) => React.ReactNode }

function KindIcon({ kind }: { kind: Transaction['kind'] }) {
  const Icon = kind === 'income' ? ArrowDownLeft : ArrowUpRight
  return (
    <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-full', kind === 'income' ? 'bg-success/12 text-success' : 'bg-destructive/10 text-destructive')}>
      <Icon className="size-4" aria-hidden />
    </span>
  )
}

/** Gelir/gider kayıtları: dar alanda kart, genişte tablo (kapsayıcı genişliğine göre). */
export function TransactionsList({ rows, canWrite, menu }: Props) {
  const { t } = useTranslation()
  const { cat, partyName } = useFinanceLookups()
  const missing = (tx: Transaction) =>
    tx.hasDocument ? null : (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive" title={t('finance.tx.missingDoc')}>
        <FileWarning className="size-3.5" aria-hidden />
        {t('finance.tx.missingDocShort')}
      </span>
    )

  return (
    <div className="@container">
      <ul className="divide-y @4xl:hidden">
        {rows.map((tx) => (
          <li key={tx.id} className="space-y-2 p-4">
            <div className="flex items-start gap-3">
              <KindIcon kind={tx.kind} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{partyName(tx.counterpartyId)}</p>
                <p className="truncate text-xs text-muted-foreground">{tx.description}</p>
              </div>
              {canWrite ? menu(tx) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2 pl-11 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <CategoryDot color={cat.get(tx.categoryId)?.color ?? 1} />
                {cat.get(tx.categoryId)?.name}
              </span>
              <span>· {fmtDate(tx.date)}</span>
              {missing(tx)}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 pl-11">
              <PayStatusBadge status={tx.status} kind={tx.kind} />
              <span className="text-right">
                <Money value={tx.total} className="block font-semibold" />
                {tx.status !== 'paid' ? <span className="text-xs text-muted-foreground">{t('finance.tx.remainingShort')} <Money value={tx.remaining} /></span> : null}
              </span>
            </div>
          </li>
        ))}
      </ul>
      <div className="hidden @4xl:block">
        <Table>
          <THead>
            <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
              <TH>{t('finance.tx.date')}</TH>
              <TH>{t('finance.tx.party')}</TH>
              <TH>{t('finance.category')}</TH>
              <TH className="hidden @5xl:table-cell">{t('finance.tx.docNo')}</TH>
              <TH className="text-right">{t('finance.tx.total')}</TH>
              <TH className="text-right">{t('finance.tx.remaining')}</TH>
              <TH>{t('finance.tx.status')}</TH>
              {canWrite ? <TH className="w-12" aria-label={t('finance.actions')} /> : null}
            </TR>
          </THead>
          <TBody>
            {rows.map((tx) => (
              <TR key={tx.id}>
                <TD className="whitespace-nowrap text-sm tabular-nums">{fmtDate(tx.date)}</TD>
                <TD className="max-w-72">
                  <div className="flex items-center gap-3">
                    <KindIcon kind={tx.kind} />
                    <div className="min-w-0">
                      <p className="truncate font-medium">{partyName(tx.counterpartyId)}</p>
                      <p className="flex items-center gap-2 truncate text-xs text-muted-foreground">
                        <span className="truncate">{tx.description}</span>
                        {missing(tx)}
                      </p>
                    </div>
                  </div>
                </TD>
                <TD>
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm">
                    <CategoryDot color={cat.get(tx.categoryId)?.color ?? 1} />
                    {cat.get(tx.categoryId)?.name}
                  </span>
                </TD>
                <TD className="hidden font-mono text-xs text-muted-foreground @5xl:table-cell">{tx.docNo || '—'}</TD>
                <TD className="whitespace-nowrap text-right font-medium">
                  <Money value={tx.total} />
                </TD>
                <TD className="whitespace-nowrap text-right text-muted-foreground">
                  <Money value={tx.remaining} />
                </TD>
                <TD>
                  <PayStatusBadge status={tx.status} kind={tx.kind} />
                </TD>
                {canWrite ? <TD className="text-right">{menu(tx)}</TD> : null}
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  )
}
